import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { MoreHorizontal, Eye, Trash2, RefreshCw } from "lucide-react";
import SortableTable from "../components/SortableTable";
import ReprocessDialog from "../components/ReprocessDialog";
import { useAuth } from "../contexts/AuthContext";
import { useFetch, useFetchMutation } from '../hooks/useFetchAuth';
import { useLoadActions } from "../hooks/useLoadActions";
import { useNavigate, useSearchParams } from "react-router-dom";

const normalizeFilters = (filters) => {
  if (!filters || typeof filters !== "object") return {};

  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      acc[key] = value.map((item) => String(item)).sort();
    }
    return acc;
  }, {});
};

const sanitizeFiltersForUrl = (filters) => {
  if (!filters || typeof filters !== "object") return {};

  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      acc[key] = value.map((item) => String(item));
    }
    return acc;
  }, {});
};

const areFilterSetsEqual = (filtersA, filtersB) => {
  const normalizedA = normalizeFilters(filtersA);
  const normalizedB = normalizeFilters(filtersB);

  const keysA = Object.keys(normalizedA);
  const keysB = Object.keys(normalizedB);

  if (keysA.length !== keysB.length) return false;

  return keysA.every((key) => {
    const valuesA = normalizedA[key];
    const valuesB = normalizedB[key];
    if (!valuesB || valuesA.length !== valuesB.length) return false;
    return valuesA.every((value, index) => value === valuesB[index]);
  });
};

function History() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsString = searchParams.toString();

  const [historyData, setHistoryData] = useState([]);
  const [reprocessDialogItem, setReprocessDialogItem] = useState(null);
  const [openMenuFor, setOpenMenuFor] = useState(null);
  
  const { deleteSheet, reprocessLoad, deletingUuid: deletingItem, reprocessingUuid: reprocessingItem } = useLoadActions();

  const { data: fetchedHistory, isLoading, error } = useFetch(['history'], '/api/history/');

  useEffect(() => {
    if (fetchedHistory?.history) {
      setHistoryData(fetchedHistory.history);
    }
  }, [fetchedHistory]);

  const urlFilters = useMemo(() => {
    const filters = {};
    const params = new URLSearchParams(searchParamsString);

    params.forEach((value, key) => {
      if (!key.startsWith("filter_")) return;

      const filterKey = key.slice("filter_".length);
      if (!filters[filterKey]) {
        filters[filterKey] = [];
      }
      filters[filterKey].push(String(value));
    });

    Object.keys(filters).forEach((key) => {
      filters[key] = filters[key].sort();
    });

    return filters;
  }, [searchParamsString]);

  const PAGE_SIZE_OPTIONS = [15, 25, 50, 100, 200];

  const urlPage = useMemo(() => {
    const p = parseInt(new URLSearchParams(searchParamsString).get("page"), 10);
    return p >= 1 ? p : 1;
  }, [searchParamsString]);

  const urlPageSize = useMemo(() => {
    const s = parseInt(new URLSearchParams(searchParamsString).get("page_size"), 10);
    return PAGE_SIZE_OPTIONS.includes(s) ? s : 15;
  }, [searchParamsString]);

  const timeZoneLabel = useMemo(() => {
    try {
      const formatter = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" });
      const parts = formatter.formatToParts(new Date());
      const tz = parts.find((part) => part.type === "timeZoneName");
      return tz ? tz.value : formatter.format(new Date()).split(" ").pop();
    } catch (e) {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
  }, []);

  const handleFiltersChange = useCallback(
    (newFilters) => {
      const sanitizedFilters = sanitizeFiltersForUrl(newFilters);
      const currentFilters = urlFilters;

      if (areFilterSetsEqual(sanitizedFilters, currentFilters)) {
        return;
      }

      const nextParams = new URLSearchParams(searchParamsString);

      Array.from(nextParams.keys())
        .filter((key) => key.startsWith("filter_"))
        .forEach((key) => nextParams.delete(key));

      Object.entries(sanitizedFilters).forEach(([key, values]) => {
        values.forEach((value) => {
          if (value !== undefined && value !== null && value !== "") {
            nextParams.append(`filter_${key}`, String(value));
          }
        });
      });

      // reset to page 1 when filters change
      nextParams.set("page", "1");

      setSearchParams(nextParams, { replace: true });
    },
    [searchParamsString, setSearchParams, urlFilters]
  );

  const handlePaginationChange = useCallback(
    (page, pageSize) => {
      const nextParams = new URLSearchParams(searchParamsString);
      const currentPage = parseInt(nextParams.get("page"), 10) || 1;
      const currentPageSize = parseInt(nextParams.get("page_size"), 10) || 50;

      if (page === currentPage && pageSize === currentPageSize) return;

      nextParams.set("page", String(page));
      nextParams.set("page_size", String(pageSize));
      setSearchParams(nextParams, { replace: true });
    },
    [searchParamsString, setSearchParams]
  );


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-history-action-menu]')) {
        setOpenMenuFor(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDelete = useCallback(
    async (item) => {
      const jobId = await deleteSheet(item);
      if (jobId) setHistoryData((prev) => prev.filter((entry) => entry.uuid !== item.uuid));
    },
    [deleteSheet]
  );

  const handleViewDetails = useCallback((historyItem) => {
    const url = `/processor/history/${historyItem.uuid}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  // The reprocess dialog shows the mapping and a preview first; the user may edit the mapping
  const handleReprocess = useCallback((item) => {
    if (!item?.uuid) return;
    setOpenMenuFor(null);
    setReprocessDialogItem(item);
  }, []);

  const startReprocess = useCallback(
    async (item, sheetsOverride) => {
      setReprocessDialogItem(null);
      const jobId = await reprocessLoad(item, sheetsOverride);
      if (!jobId) return;
      setHistoryData((prev) =>
        prev.filter(
          (entry) =>
            !(
              entry.filename === item.filename &&
              entry.shop_sign === item.shop_sign &&
              entry.client === item.client &&
              entry.year === item.year &&
              entry.week === item.week &&
              (entry.category ?? null) === (item.category ?? null)
            )
        )
      );
    },
    [reprocessLoad]
  );

  const columns = useMemo(() => {
    const baseColumns = [
      {
        key: "created_at",
        label: "Created At",
        render: (item) => {
          if (!item.created_at) return "N/A";
          try {
            const date = new Date(item.created_at);
            return date.toLocaleString("fr-FR", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            });
          } catch (e) {
            console.error("Error formatting date:", e);
            return item.created_at;
          }
        },
        rawValue: (item) => (item.created_at ? new Date(item.created_at).getTime() : 0),
        filterable: false,
      },
      { key: "shop_sign", label: "Shop Sign", render: (item) => item.shop_sign || "N/A" },
      { key: "client", label: "Client", render: (item) => item.client || "N/A",initialWidth: 100},
      { key: "year", label: "Year", render: (item) => item.year || "N/A" },
      { key: "week", label: "Week", render: (item) => item.week || "N/A" },
      { key: "category", label: "Univers", render: (item) => item.category || "--" },
      { key: "filename", label: "Filename", render: (item) => item.filename || "N/A", initialWidth: 150 },
      { key: "sheet_name", label: "Sheet Name", render: (item) => item.sheet_name || "N/A" },
      {
        key: "rows_count",
        label: "Rows",
        render: (item) => item.rows_count ?? "N/A",
        rawValue: (item) => item.rows_count || 0,
      },
      {
        key: "columns_count",
        label: "Cols",
        render: (item) => item.columns_count ?? "N/A",
        rawValue: (item) => item.columns_count || 0,
      },
      { key: "status", label: "Status", render: (item) => item.status ?? "N/A" },
      {
        key: "actions",
        label: "Actions",
        render: (item) => {
          const isDeleting = deletingItem === item.uuid;
          const isMenuOpen = openMenuFor === item.uuid;

          const isReprocessing = reprocessingItem === item.uuid;

          return (
            <div className="relative" data-history-action-menu>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenuFor((prev) => (prev === item.uuid ? null : item.uuid));
                }}
                className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <span className="inline-flex items-center gap-2">
                  <MoreHorizontal className="w-4 h-4" />
                  Actions
                </span>
              </button>

              {isMenuOpen && (
                <div
                  className="absolute right-0 mt-2 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-lg z-20"
                  data-history-action-menu
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuFor(null);
                      handleViewDetails(item);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <span className="inline-flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      Voir les détails
                    </span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReprocess(item);
                    }}
                    disabled={isReprocessing}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      isReprocessing ? "text-gray-400 cursor-not-allowed" : "text-orange-600 dark:text-orange-400"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <RefreshCw className="w-4 h-4" />
                      {isReprocessing ? "Retraitement..." : "Retraiter"}
                    </span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuFor(null);
                      handleDelete(item);
                    }}
                    disabled={isDeleting}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      isDeleting ? "text-gray-400 cursor-not-allowed" : "text-red-600 dark:text-red-400"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Trash2 className="w-4 h-4" />
                      {isDeleting ? "Suppression…" : "Supprimer"}
                    </span>
                  </button>
                </div>
              )}
            </div>
          );
        },
        filterable: false,
        sortable: false,
        allowOverflow: true,
      },
    ];

    if (currentUser?.isadmin) {
      baseColumns.splice(1, 0, {
        key: "username",
        label: "Created By",
        render: (item) => (
          <div className="flex items-center">
            <span>{item.username || "N/A"}</span>
          </div>
        ),
        rawValue: (item) => item.username || "",
      });
    }

    return baseColumns;
  }, [currentUser?.isadmin, deletingItem, reprocessingItem, openMenuFor, handleDelete, handleViewDetails, handleReprocess]);

  return (
    <div className="mx-auto py-8">
      <div className="max-w-[110rem] min-h-[40rem] mx-auto bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex justify-between items-center">
          <div className="flex items-start gap-3 flex-col">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Historique</h2>
            <span className="align-middle text-sm text-gray-500 dark:text-gray-400">Fuseau horaire {timeZoneLabel}</span>
          </div>
          <div>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Retour
            </button>
          </div>
        </div>

        <div className="flex flex-col">
          {isLoading && (
            <div className="flex justify-center items-center py-8">
              <div className="w-10 h-10 border-4 border-blue-500 dark:border-blue-400 border-t-transparent rounded-full animate-spin"></div>
              <p className="ml-3 text-gray-600 dark:text-gray-300">Loading history...</p>
            </div>
          )}

          {error && (
            <p className="text-center text-red-500 dark:text-red-400">Error loading history: {error}</p>
          )}

          {!isLoading && !error && (
            <SortableTable
              data={historyData || []}
              columns={columns}
              emptyMessage="No history found."
              initialSortBy={{ key: "created_at", direction: "descending" }}
              exportable={true}
              exportFilename="processing_history.csv"
              maxHeight="60vh"
              density="compact"
              wrap={false}
              max_col_width={150}
              minHeight="65vh"
              initialFilters={urlFilters}
              onFiltersChange={handleFiltersChange}
              pagination={true}
              initialPage={urlPage}
              defaultPageSize={urlPageSize}
              onPaginationChange={handlePaginationChange}
            />
          )}
        </div>
        {reprocessDialogItem && (
          <ReprocessDialog
            item={reprocessDialogItem}
            onCancel={() => setReprocessDialogItem(null)}
            onConfirm={(sheetsOverride) => startReprocess(reprocessDialogItem, sheetsOverride)}
          />
        )}
      </div>
    </div>
  );
}

export default History;
